import Box from "@mui/material/Box";
import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useAppDispatch } from "../../../app/hooks";
import {
  getSupplierBatchesData,
} from "../../../features/supplier/supplierSlice";
import { useCallback, useEffect, useState } from "react";
import React from "react";
import WarningIcon from "../../IconComp/warningIcon";
import { ColorPallet } from "../../../utils/colorPallet";
import { SupplierDetailHeader } from "../../../utils/constants";
import LoadingSpinner from "../../common/Loading";

interface IProps {
  supplierId?: any;
}
const SupplierDetailMedModal = ({ supplierId }: IProps) => {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(true); // Add loading state
  const [supplierMedBatches, setSupplierMedBatches] = useState<any>(); // Add loading state

  const [error, setError] = useState<any>("");

  const pharmacyId = localStorage?.getItem("pharmacyId");



  const getSupplierMedicineBatches = useCallback(async () => {
    const dto = {
      pharmacy_id: pharmacyId,
      supplier_id: supplierId,
    };

    try {
      setIsLoading(true);

      const res = await dispatch(getSupplierBatchesData(dto));
 

      if (getSupplierBatchesData.rejected.match(res)) {
        // If the thunk was rejected with a value (e.g., a message)
        setError(res.payload || "Failed to fetch supplier batches");
        setSupplierMedBatches([]); // Optionally clear previous data
      } else if (res.payload) {
        setSupplierMedBatches(res.payload.existing_supplier_batch);
        setError(""); // Clear any previous errors
      }
    } catch (error) {
      console.error("Error fetching supplier batches:", error);
      setError("Unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, pharmacyId, supplierId]); // ✅ added supplierId to dependencies

  useEffect(() => {
    getSupplierMedicineBatches();
  }, [getSupplierMedicineBatches]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const isExpired = (expiryDate: string) => {
    return new Date(expiryDate) < new Date();
  };
  const commonCellStyles = {
    borderBottom: `1px solid ${ColorPallet.borderColor}`,
    borderLeft: `1px solid ${ColorPallet.borderColor}`,
    borderRight: `1px solid ${ColorPallet.borderColor}`,
    fontSize: "14px",
    fontWeight: 400,
  };

  return (
    <>
      {/* Supplier Batches Table */}
      {isLoading ? (
        <Box sx={{ py: 8 }}>
          <LoadingSpinner />
        </Box>
      ) : (
        <>
          <Box
            sx={{
              backgroundColor: "#0B2238",
              color: "white",
              padding: "8px 12px",
              fontSize: "16px",
              fontWeight: 500,
              mt: "8px",
            }}
          >
            Supplier Batches
          </Box>

          <Table
            size="small"
            sx={{ border: `1px solid ${ColorPallet.borderColor}` }}
          >
            <TableHead sx={{ backgroundColor: ColorPallet.tableHeaderColor }}>
              <TableRow>
                {SupplierDetailHeader?.map((headerItem: any, index: any) => (
                  <TableCell
                    key={index}
                    sx={{
                      borderBottom: `1px solid ${ColorPallet.borderColor}`,
                      borderLeft: `1px solid ${ColorPallet.borderColor}`,
                      borderRight: `1px solid ${ColorPallet.borderColor}`,
                    }}
                  >
                    {headerItem.title}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {supplierMedBatches && supplierMedBatches.length > 0 ? (
                supplierMedBatches?.map((batch: any) => (
                  <TableRow key={batch.id}>
                    <TableCell sx={commonCellStyles}>
                      <Box
                        sx={{
                          color: "#0A8DD0",
                          fontSize: "12px",
                          fontWeight: 500,
                        }}
                      >
                        {batch.batch_no ?? "-"}
                      </Box>
                    </TableCell>
                    <TableCell sx={commonCellStyles}>
                      {batch.pharmacy_medicine?.name ?? "-"}
                      <span style={{ fontSize: "10px", marginLeft: "2px" }}>
                        {batch.pharmacy_medicine?.dose}
                      </span>
                    </TableCell>
                    <TableCell sx={commonCellStyles}>
                      {batch.total_box ?? "-"}
                    </TableCell>
                    <TableCell sx={commonCellStyles}>
                      {batch.unit_per_box ?? "-"}
                    </TableCell>
                    <TableCell sx={commonCellStyles}>
                      {batch.quantity ?? "-"}
                    </TableCell>
                    <TableCell sx={commonCellStyles}>
                      {(batch.quantity-batch.quantity_sold) ?? "-"}
                    </TableCell>
                    <TableCell sx={commonCellStyles}>
                      {batch.sell_price != null
                        ? `Rs ${batch.sell_price}`
                        : "-"}
                    </TableCell>
                    <TableCell sx={commonCellStyles}>
                      {batch.shelf?.name ?? "-"}
                    </TableCell>
                    <TableCell sx={commonCellStyles}>
                      {isExpired(batch.expiry_date) ? (
                        <Tooltip title="Expired">
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <WarningIcon />
                            {formatDate(batch.expiry_date) ?? "-"}
                          </Box>
                        </Tooltip>
                      ) : (
                        formatDate(batch.expiry_date)
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                    {error
                      ? error
                      : "No batches have been added for this supplier yet."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </>
      )}
    </>
  );
};

export default SupplierDetailMedModal;
